import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Search, Copy, ExternalLink, Upload, Trash2, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Money, ItemsEditor, StatusPill, NativeSelect } from "@/components/common";
import { StandingEditor, EMPTY_STANDING, standingText } from "@/components/StandingEditor";
import { api, errMsg } from "@/lib/api";
import { portalUrl, mapsHref } from "@/lib/format";
import { CustomerImportDialog } from "@/components/CustomerImportDialog";

const EMPTY = { name: "", phone: "", address: "", area: "", route_order: 0, credit_limit: 2000, default_items: [], state: "", notes: "", active: true, standing_order: EMPTY_STANDING, birthday: "", gstin: "", maps_url: "", email: "" };

function CustomerDialog({ cust, products, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [socs, setSocs] = useState([]);
  useEffect(() => { api.get("/societies").then((r) => setSocs(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (cust) setF({ ...EMPTY, ...cust, standing_order: cust.standing_order || EMPTY_STANDING }); }, [cust]);
  const save = async () => {
    try {
      const body = { ...EMPTY, ...f, route_order: +f.route_order || 0, credit_limit: +f.credit_limit || 0 };
      Object.keys(body).forEach((k) => !(k in EMPTY) && delete body[k]);
      cust.id ? await api.put(`/customers/${cust.id}`, body) : await api.post("/customers", body);
      toast.success("Customer saved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label, type = "text") => (
    <div className="space-y-1"><Label>{label}</Label><Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`customer-${k}-input`} /></div>
  );
  return (
    <Dialog open={!!cust} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader><DialogTitle className="font-display">{cust?.id ? "Edit" : "Add"} customer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field("name", "Name")}
          {field("phone", "WhatsApp number")}
          <div className="col-span-2 space-y-1"><Label>Address</Label><Textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="customer-address-input" /></div>
          <div className="col-span-2 space-y-1"><Label>GSTIN (B2B customers only)</Label><Input value={f.gstin || ""} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1Z5" data-testid="customer-gstin-input" /></div>
          {field("area", "Area / route")}
          {field("route_order", "Stop # on route", "number")}
          {field("credit_limit", "Credit limit (₹)", "number")}
          {field("notes", "Notes")}
          {field("birthday", "Birthday (DD-MM, optional)")}
          {field("email", "Email (payment receipts are emailed here)")}
          <div className="col-span-2">{field("maps_url", "Google Maps link of the home (used for delivery)")}</div>
          <div className="space-y-1"><Label>Society</Label>
            <NativeSelect value={f.society_id || ""} onChange={(e) => setF({ ...f, society_id: e.target.value })} data-testid="customer-society-select">
              <option value="">Not in a society</option>{socs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </NativeSelect></div>
          {field("tower", "Tower / block")}
          {field("flat_no", "Flat no.")}
        </div>
        <div>
          <Label>Usual order (pre-filled on their WhatsApp form)</Label>
          <div className="mt-2"><ItemsEditor products={products} value={f.default_items} onChange={(v) => setF({ ...f, default_items: v })} prefix="default" /></div>
        </div>
        <StandingEditor value={f.standing_order} onChange={(v) => setF({ ...f, standing_order: v })} products={products} prefix="cust-standing" />
        <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="customer-save-btn">Save customer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [importing, setImporting] = useState(false);
  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)); }, []);
  const list = rows.filter((c) => `${c.name} ${c.phone} ${c.area}`.toLowerCase().includes(q.toLowerCase()));
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Customers with bills or orders are made inactive instead, so their history is kept.`)) return;
    try { const r = await api.delete(`/customers/${c.id}`); toast.success(r.data.deleted ? `${c.name} deleted` : `${c.name} has bills, so made inactive instead`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="customers-page">
      <PageHeader eyebrow="Customers" title="Door-to-door customers" subtitle={`${rows.filter((c) => c.active).length} active. Each customer has a personal link for orders, delivery proof and payments.`}>
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-56 bg-white" data-testid="customer-search" /></div>
        <Button variant="outline" className="rounded-full border-[#0F382C]/30 text-[#0F382C]" onClick={() => setImporting(true)} data-testid="import-customers-btn"><Upload className="mr-1 h-4 w-4" />Import Excel</Button>
        <Button className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setEdit({})} data-testid="add-customer-btn"><Plus className="mr-1 h-4 w-4" />Add customer</Button>
      </PageHeader>
      <CustomerImportDialog open={importing} onOpenChange={setImporting} onDone={load} />
      <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Area</TableHead><TableHead className="text-right">Balance due</TableHead><TableHead className="text-right">Limit</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {list.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className={c.active ? "" : "opacity-50"}>
                <TableCell><Link to={`/customers/${c.id}`} className="font-semibold hover:underline" data-testid={`customer-link-${c.id}`}>{c.name}</Link><div className="text-xs text-muted-foreground">{c.phone}</div></TableCell>
                <TableCell>{c.area} <span className="text-xs text-muted-foreground">#{c.route_order}</span> {!c.active && <StatusPill status="skipped" label="Inactive" />}
                  {c.standing_order?.active && <div className="text-xs text-emerald-700 mt-0.5" data-testid={`standing-label-${c.id}`}>Standing: {standingText(c.standing_order, products)}</div>}
                </TableCell>
                <TableCell className="text-right"><Money value={c.balance} className={c.balance > c.credit_limit ? "text-rose-600 font-semibold" : c.balance > 0 ? "text-amber-700 font-semibold" : ""} /></TableCell>
                <TableCell className="text-right"><Money value={c.credit_limit} className="text-muted-foreground" /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard?.writeText(portalUrl(c.token)); toast.success("Customer link copied"); }} data-testid={`copy-portal-${c.id}`}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" asChild><a href={portalUrl(c.token)} target="_blank" rel="noreferrer" data-testid={`open-portal-${c.id}`}><ExternalLink className="h-4 w-4" /></a></Button>
                  {(c.maps_url || c.lat != null) && <Button variant="ghost" size="icon" asChild><a href={mapsHref(c)} target="_blank" rel="noreferrer" aria-label="Open in Google Maps" data-testid={`maps-customer-${c.id}`}><Navigation className="h-4 w-4 text-emerald-700" /></a></Button>}
                  <Button variant="ghost" size="icon" onClick={() => setEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} aria-label="Delete customer" data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CustomerDialog cust={edit} products={products} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
